import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { MessageSquare, Quote, Loader2, CheckCircle2 } from "lucide-react";

const emptyForm = {
  message: "",
  display_role: "",
  display_location: "",
  submitter_email: "",
  consent_public: false,
};

export default function FeedbackSection() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api
      .get("/feedback/public")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please write at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        message: form.message.trim(),
        display_role: form.display_role.trim() || null,
        display_location: form.display_location.trim() || null,
        submitter_email: form.submitter_email.trim() || null,
        consent_public: !!form.consent_public,
      });
      setSubmitted(true);
      setForm(emptyForm);
      toast.success("Thank you — your feedback has been received.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-slate-50 border-y border-slate-200" data-testid="feedback-section">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">
            Clinician feedback
          </div>
          <h2 className="font-heading text-3xl font-bold text-[#111827] mt-2">
            Built with, and for, South African clinicians.
          </h2>
          <p className="text-sm text-slate-600 mt-3">
            We are still early. If RapidCare SA has helped you — or if something needs improving — tell us below.
            Approved feedback may be shown here anonymously after manual review.
          </p>
        </div>

        {items.length > 0 && (
          <div className="grid md:grid-cols-3 gap-5 mb-12" data-testid="feedback-public-list">
            {items.map((t) => (
              <Card key={t.id} className="border-slate-200" data-testid={`feedback-item-${t.id}`}>
                <CardContent className="p-6">
                  <Quote className="w-4 h-4 text-[#111827] mb-3" />
                  <blockquote className="text-[#111827] font-heading text-lg leading-snug italic">
                    “{t.message}”
                  </blockquote>
                  {(t.display_role || t.display_location) && (
                    <div className="mt-4 text-xs tracking-widest uppercase text-slate-500 font-semibold">
                      {[t.display_role, t.display_location].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="border-slate-200 max-w-3xl mx-auto">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="w-5 h-5 text-[#111827]" />
              <h3 className="font-heading font-semibold text-[#111827] text-lg">
                Share your feedback
              </h3>
            </div>

            {submitted ? (
              <div
                className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
                data-testid="feedback-thankyou"
              >
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold">Thank you.</div>
                  <div className="text-emerald-700">
                    Your feedback has been received. If you ticked the consent box and it is suitable, it may be posted here anonymously after review. We do not share or publish submitter details.
                  </div>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => setSubmitted(false)}
                    data-testid="feedback-submit-another"
                  >
                    Submit more feedback
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
                <div>
                  <Label htmlFor="fb-message">Your feedback</Label>
                  <Textarea
                    id="fb-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us what is working, what is broken, or what you would like to see next."
                    rows={5}
                    maxLength={1000}
                    className="mt-1.5"
                    required
                    data-testid="feedback-input-message"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    {form.message.length}/1000 characters
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fb-role">Your role (optional)</Label>
                    <Input
                      id="fb-role"
                      value={form.display_role}
                      onChange={(e) => setForm({ ...form, display_role: e.target.value })}
                      placeholder="e.g. Medical officer, Registrar, GP, Nurse, Student"
                      className="mt-1.5"
                      maxLength={80}
                      data-testid="feedback-input-role"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fb-location">Province / setting (optional)</Label>
                    <Input
                      id="fb-location"
                      value={form.display_location}
                      onChange={(e) => setForm({ ...form, display_location: e.target.value })}
                      placeholder="e.g. Gauteng · Regional hospital"
                      className="mt-1.5"
                      maxLength={80}
                      data-testid="feedback-input-location"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="fb-email">Email (optional, kept private)</Label>
                  <Input
                    id="fb-email"
                    type="email"
                    value={form.submitter_email}
                    onChange={(e) => setForm({ ...form, submitter_email: e.target.value })}
                    placeholder="so we can reply if needed — never published"
                    className="mt-1.5 ph-no-capture"
                    data-ph-no-capture="true"
                    data-testid="feedback-input-email"
                  />
                </div>

                <label className="flex items-start gap-3 rounded-md bg-slate-50 border border-slate-200 p-3">
                  <Checkbox
                    checked={form.consent_public}
                    onCheckedChange={(v) => setForm({ ...form, consent_public: !!v })}
                    data-testid="feedback-consent"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    I consent to this feedback being published anonymously on the RapidCare SA
                    landing page if an admin chooses to. Only the message and optional role /
                    location above will be shown. My email is never published.
                  </span>
                </label>

                <div className="pt-1 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-500">
                    Nothing is shown publicly until manually reviewed and approved.
                  </div>
                  <Button
                    type="submit"
                    className="bg-[#0B5FFF] hover:bg-[#0A54E0]"
                    disabled={submitting}
                    data-testid="feedback-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending
                      </>
                    ) : (
                      "Send feedback"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
